/**
 * Porządkowanie tekstów alertów na karcie zlecenia przy wyświetlaniu
 * (poprawa brzmienia 01.10.2026).
 *
 * Dziennika (`UserOfferChange`) nie przepisujemy: wpisy zapisane przed
 * poprawką brzmienia API mają dawny zapis — małą literą, z łańcuchami
 * „X: Y; Z” i dopiskami „— oszacowanie (oszacowanie)”. Karta pokazuje je
 * zdaniami. Tu są tylko narzędzia ogólne; znane stare wzorce rozpoznaje
 * każdy alert u siebie (`ReplanAlerts.tsx`, `TmsChangeAlerts.tsx`).
 *
 * Wpis zaczynający się wielką literą traktujemy jako nowe brzmienie i nie
 * ruszamy go — stare wpisy zaczynały się zawsze małą literą.
 */

/** Czy tekst zaczyna się małą literą — znak dawnego zapisu wpisów. */
export function startsLowercase(text: string): boolean {
  const first = text.trimStart().charAt(0);
  return first !== '' && first !== first.toUpperCase() && first === first.toLowerCase();
}

/** Pierwsza litera wielka; reszta bez zmian. */
export function capitalize(text: string): string {
  const t = text.trim();
  return t === '' ? t : t.charAt(0).toUpperCase() + t.slice(1);
}

/** Zdanie: wielka litera na początku i kropka na końcu (chyba że już jest znak końca). */
export function asSentence(text: string): string {
  const t = capitalize(text);
  if (t === '') return t;
  return /[.!?…]$/.test(t) ? t : `${t}.`;
}

/**
 * Dzieli tekst po separatorze („; ”), ale nie wewnątrz nawiasów — w starym
 * uzasadnieniu „(krócej o ok. 400 km, 80%; próg: 100 km i 30%)” to jedna
 * część. Puste części pomija.
 */
export function splitOutsideParens(text: string, separator = '; '): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  let i = 0;
  while (i < text.length) {
    const ch = text.charAt(i);
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (depth === 0 && text.startsWith(separator, i)) {
      parts.push(text.slice(start, i));
      i += separator.length;
      start = i;
      continue;
    }
    i += 1;
  }
  parts.push(text.slice(start));
  return parts.map((p) => p.trim()).filter((p) => p !== '');
}

/**
 * Dawne dopiski o szacunku → jedno „(szacunek)”: „— oszacowanie
 * (oszacowanie)”, „— oszacowanie” i „(oszacowanie)” na końcu części.
 */
export function hedgeAsEstimate(text: string): string {
  return text
    .replace(/\s*—\s*oszacowanie\s*\(oszacowanie\)/g, ' (szacunek)')
    .replace(/\s*—\s*oszacowanie\b/g, ' (szacunek)')
    .replace(/\s*\(oszacowanie\)/g, ' (szacunek)')
    .trim();
}
